import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CustomNumericKeypad } from './CustomNumericKeypad';
import { Camera, Package, Zap, X, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FastTrackProductEntryProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (product: {
    barcode: string;
    name: string;
    boxes: number;
    unitsPerBox: number;
    photos: string[];
    creationSource: 'scan';
  }) => Promise<boolean>;
}

type Step = 'barcode' | 'photo' | 'boxes' | 'unitsPerBox' | 'complete';

export const FastTrackProductEntry = ({
  isOpen,
  onClose,
  onAddProduct
}: FastTrackProductEntryProps) => {
  const [currentStep, setCurrentStep] = useState<Step>('barcode');
  const [barcode, setBarcode] = useState('');
  const [productName, setProductName] = useState('');
  const [boxes, setBoxes] = useState('');
  const [unitsPerBox, setUnitsPerBox] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');
  
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      // Focus barcode input after a brief delay
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle barcode scan detection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || currentStep !== 'barcode') return;
      
      if (e.key === 'Enter' && barcode.length > 0) {
        e.preventDefault();
        handleBarcodeDetected();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, barcode]);

  // Countdown effect for photo capture
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && currentStep === 'photo') {
      // Auto-trigger camera after countdown
      triggerCamera();
    }
  }, [countdown, currentStep]);

  const resetForm = () => {
    setCurrentStep('barcode');
    setBarcode('');
    setProductName('');
    setBoxes('');
    setUnitsPerBox('');
    setPhotoUrl('');
    setIsProcessing(false);
    setCountdown(0);
    setUploadStatus('');
    setUploadError('');
  };

  const handleBarcodeDetected = () => {
    if (barcode.length < 3) {
      toast({
        title: "Invalid Barcode",
        description: "Please scan a valid barcode",
        variant: "destructive",
      });
      return;
    }

    // Generate product name from barcode for now
    setProductName(`Product ${barcode}`);
    setCurrentStep('photo');
    setCountdown(3); // 3-second countdown
  };

  const triggerCamera = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setUploadStatus('Preparing upload...');
    setUploadError('');
    
    try {
      console.log('📸 Starting photo capture and upload process');
      setUploadStatus('Validating file...');
      
      // Additional client-side validation
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }
      
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image must be smaller than 5MB');
      }
      
      setUploadStatus('Uploading to storage...');
      console.log('📤 Uploading file:', file.name, file.size, 'bytes');
      
      // Import the upload utility
      const { uploadProductImage } = await import('@/utils/imageUpload');
      const result = await uploadProductImage(file, barcode);
      
      if (result.success) {
        console.log('✅ Upload successful:', result.url);
        setPhotoUrl(result.url);
        setUploadStatus('Upload complete!');
        
        toast({
          title: "Photo uploaded successfully",
          description: "Photo has been saved and is ready to use",
        });
        
        // Auto-advance after short delay to show success
        setTimeout(() => {
          setCurrentStep('boxes');
          setUploadStatus('');
        }, 1000);
      } else {
        console.error('❌ Upload failed:', result.error);
        setUploadError(result.error || 'Upload failed');
        setUploadStatus('');
        
        toast({
          title: "Upload failed",
          description: result.error || "Failed to upload photo",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('❌ Photo capture error:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to upload photo";
      setUploadError(errorMessage);
      setUploadStatus('');
      
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNumberPress = (number: string) => {
    console.log('handleNumberPress called:', { number, currentStep, boxes, unitsPerBox });
    if (currentStep === 'boxes') {
      setBoxes(prev => {
        const newValue = prev + number;
        console.log('Setting boxes:', prev, '+', number, '=', newValue);
        return newValue;
      });
    } else if (currentStep === 'unitsPerBox') {
      setUnitsPerBox(prev => {
        const newValue = prev + number;
        console.log('Setting unitsPerBox:', prev, '+', number, '=', newValue);
        return newValue;
      });
    }
  };

  const handleBackspace = () => {
    if (currentStep === 'boxes') {
      setBoxes(prev => prev.slice(0, -1));
    } else if (currentStep === 'unitsPerBox') {
      setUnitsPerBox(prev => prev.slice(0, -1));
    }
  };

  const handleConfirm = () => {
    if (currentStep === 'boxes') {
      if (parseInt(boxes) > 0) {
        setCurrentStep('unitsPerBox');
      }
    } else if (currentStep === 'unitsPerBox') {
      if (parseInt(unitsPerBox) > 0) {
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    console.log('🔍 FastTrack handleSubmit started:', {
      barcode,
      productName,
      boxes,
      unitsPerBox,
      photoUrl,
      isProcessing
    });

    if (isProcessing) {
      console.log('⚠️ Already processing, ignoring duplicate submission');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Validate required fields
      if (!barcode || !productName || !boxes || !unitsPerBox) {
        console.error('❌ Missing required fields:', {
          barcode: !!barcode,
          productName: !!productName,
          boxes: !!boxes,
          unitsPerBox: !!unitsPerBox
        });
        throw new Error('Missing required product information');
      }
      // Require a photo for Fast Track submissions
      if (!photoUrl) {
        console.error('❌ Photo required for Fast Track submission');
        toast({
          title: "Photo Required",
          description: "Please capture a product photo before continuing.",
          variant: "destructive",
        });
        setCurrentStep('photo');
        return;
      }

      console.log('🔍 Calling onAddProduct with data:', {
        barcode,
        name: productName,
        boxes: parseInt(boxes),
        unitsPerBox: parseInt(unitsPerBox),
        photos: photoUrl ? [photoUrl] : [],
        creationSource: 'scan'
      });

      const success = await onAddProduct({
        barcode,
        name: productName,
        boxes: parseInt(boxes),
        unitsPerBox: parseInt(unitsPerBox),
        photos: photoUrl ? [photoUrl] : [],
        creationSource: 'scan'
      });

      console.log('🔍 onAddProduct result:', { success });

      if (success) {
        console.log('✅ Product added successfully, moving to complete step');
        setCurrentStep('complete');
        toast({
          title: "Product Added Successfully",
          description: `${productName} has been added to the order`,
        });
        
        // Auto-reset after 2 seconds for next scan
        setTimeout(() => {
          console.log('🔄 Auto-resetting form for next product');
          resetForm();
          barcodeInputRef.current?.focus();
        }, 2000);
      } else {
        console.error('❌ onAddProduct returned false - product not added');
        throw new Error('Product could not be added to the order');
      }
    } catch (error: any) {
      console.error('❌ FastTrack submission error:', {
        error: error.message || error,
        stack: error.stack,
        barcode,
        productName
      });
      
      toast({
        title: "Failed to Add Product",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      console.log('🔍 FastTrack handleSubmit completed, setting isProcessing to false');
      setIsProcessing(false);
    }
  };

  const getStepIndicator = () => {
    const steps = ['barcode', 'photo', 'boxes', 'unitsPerBox'];
    const currentIndex = steps.indexOf(currentStep);
    
    return (
      <div className="flex justify-center space-x-2">
        {steps.map((step, index) => (
          <div
            key={step}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${
              index <= currentIndex ? 'bg-primary' : 'bg-muted'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full h-full max-w-none max-h-none m-0 rounded-none flex flex-col p-0 border-0 z-modal overflow-hidden bg-background">
        {/* Mobile-optimized header */}
        <div className="flex-shrink-0 p-4 border-b bg-background/95 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <Zap className="w-5 h-5" />
              Fast Add Product
            </DialogTitle>
            {/* Prominent mobile close button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onClose}
              className="h-9 w-9 p-0 bg-background shadow-sm border-border/50 hover:bg-muted"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="mt-3">
            {getStepIndicator()}
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center space-y-6 p-4 overflow-auto overscroll-contain pb-safe"
             style={{ touchAction: 'pan-y' }}>
          {/* Step 1: Barcode Scan */}
          {currentStep === 'barcode' && (
            <div className="text-center space-y-4">
              <Package className="w-16 h-16 mx-auto text-muted-foreground" />
              <h2 className="text-2xl font-semibold">Scan Barcode</h2>
              <p className="text-muted-foreground">Ready to scan product barcode</p>
              
              <Input
                ref={barcodeInputRef}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Barcode will appear here..."
                className="text-center text-lg"
                autoFocus
              />
              
              {barcode && (
                <Button onClick={handleBarcodeDetected} size="lg" className="w-full">
                  Continue with {barcode}
                </Button>
              )}
            </div>
          )}

          {/* Step 2: Photo Countdown */}
          {currentStep === 'photo' && countdown > 0 && (
            <div className="text-center space-y-4">
              <Camera className="w-16 h-16 mx-auto text-muted-foreground" />
              <h2 className="text-2xl font-semibold">Photo in {countdown}</h2>
              <p className="text-muted-foreground">Get ready to take product photo</p>
              <Button onClick={() => setCountdown(0)} size="lg">
                Take Photo Now
              </Button>
            </div>
          )}

          {/* Step 2: Photo Capture */}
          {currentStep === 'photo' && countdown === 0 && (
            <div className="text-center space-y-4">
              <Camera className="w-16 h-16 mx-auto text-muted-foreground" />
              <h2 className="text-2xl font-semibold">Take Photo</h2>
              <p className="text-muted-foreground">Capture product image</p>
              
              {photoUrl && (
                <div className="mx-auto w-24 h-24 rounded-lg overflow-hidden border-2 border-green-500">
                  <img 
                    src={photoUrl} 
                    alt="Captured product" 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="text-sm text-muted-foreground">
                      {uploadStatus || 'Processing...'}
                    </span>
                  </div>
                </div>
              )}
              
              {uploadError && (
                <div className="text-center text-sm text-destructive bg-destructive/10 rounded-lg p-3">
                  {uploadError}
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
                disabled={isProcessing}
              />
              
              {!photoUrl ? (
                <Button 
                  onClick={triggerCamera} 
                  size="lg" 
                  className="w-full"
                  disabled={isProcessing}
                >
                  <Camera className="w-5 h-5 mr-2" />
                  {isProcessing ? 'Uploading...' : 'Open Camera'}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button 
                    onClick={() => setCurrentStep('boxes')}
                    size="lg" 
                    className="w-full"
                  >
                    Continue with Photo
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setUploadError('');
                      setUploadStatus('');
                      triggerCamera();
                    }} 
                    size="lg" 
                    className="w-full"
                    disabled={isProcessing}
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Retake Photo
                  </Button>
                </div>
              )}
              
            </div>
          )}

          {/* Step 3: Boxes */}
          {currentStep === 'boxes' && (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-semibold">Number of Boxes</h2>
                <div className="text-3xl font-bold my-4 min-h-[48px] flex items-center justify-center">
                  {boxes || '0'}
                </div>
              </div>
              
                <CustomNumericKeypad
                  onNumberPress={handleNumberPress}
                  onBackspace={handleBackspace}
                  onConfirm={handleConfirm}
                  numberButtonsDisabled={isProcessing}
                  confirmButtonDisabled={!boxes || parseInt(boxes) === 0 || isProcessing}
                />
            </div>
          )}

          {/* Step 4: Units per Box */}
          {currentStep === 'unitsPerBox' && (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-semibold">Units per Box</h2>
                <div className="text-3xl font-bold my-4 min-h-[48px] flex items-center justify-center">
                  {unitsPerBox || '0'}
                </div>
                <p className="text-sm text-muted-foreground">
                  {boxes} boxes × {unitsPerBox || '0'} = {(parseInt(boxes) || 0) * (parseInt(unitsPerBox) || 0)} total units
                </p>
              </div>
              
                <CustomNumericKeypad
                  onNumberPress={handleNumberPress}
                  onBackspace={handleBackspace}
                  onConfirm={handleConfirm}
                  numberButtonsDisabled={isProcessing}
                  confirmButtonDisabled={!unitsPerBox || parseInt(unitsPerBox) === 0 || isProcessing}
                />
            </div>
          )}

          {/* Step 5: Complete */}
          {currentStep === 'complete' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-semibold text-green-600">Product Added!</h2>
              <p className="text-muted-foreground">Ready for next scan...</p>
            </div>
          )}
        </div>

        {/* Footer with product preview */}
        {(currentStep === 'boxes' || currentStep === 'unitsPerBox') && (
          <div className="flex-shrink-0 mt-4 p-4 pb-safe bg-muted/50 border-t sticky bottom-0 bg-background/95 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              {photoUrl && (
                <img src={photoUrl} alt="Product" className="w-12 h-12 object-cover rounded border" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{productName}</p>
                <p className="text-sm text-muted-foreground truncate">Barcode: {barcode}</p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};